import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  deleteSavedFilter,
  listSavedFilters,
  listSubmissions,
  moderateSubmission,
  saveSavedFilter,
  updateMetadataBulk,
} from '../lib/api';
import type {
  ArchiveRecord,
  ListSubmissionsRequest,
  ListSubmissionsSort,
  SavedFilter,
} from '../types/moderation';
import Checkbox from './ui/Checkbox';
import Select from './ui/Select';
import { SkeletonRow } from './ui/Skeleton';
import Skeleton from './ui/Skeleton';
import EmptyState from './ui/EmptyState';
import Tooltip from './ui/Tooltip';
import SavedFiltersBar from './SavedFiltersBar';
import StatusBadge from './StatusBadge';
import ErrorState from './ErrorState';
import { formatDate, formatDateTime, relativeTime } from '../lib/format';
import { setSubmissionNavCache } from '../lib/submission-nav';
import { REJECT_REASON_TEMPLATES } from '../lib/reason-templates';
import { toast } from '../lib/toast';
import { useCurrentUser } from '../lib/auth';
import { buildCsv, downloadCsv } from '../lib/csv';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './ui/Dialog';

interface SubmissionListProps {
  onSelect: (submissionId: string) => void;
  initialFilters?: ListSubmissionsRequest;
}

const STATUS_OPTIONS = [
  'Any status',
  'pending_review',
  'approved',
  'rejected',
  'withdrawn',
  'awaiting_contributor',
];

const THEME_OPTIONS = [
  'Any theme',
  'Community',
  'Activism',
  'Arts & Culture',
  'Sport',
];

// F-307: reverse the decade options so the most active decades (2020s,
// 2010s) sit at the top of the dropdown. Most moderation traffic is on
// recent submissions.
const DECADE_OPTIONS = [
  'Any decade',
  '2020s',
  '2010s',
  '2000s',
  '1990s',
  '1980s',
  '1970s',
  'Pre-1970s',
];

const MEDIA_TYPE_OPTIONS = [
  'Any media',
  'Photo',
  'Video',
  'Audio',
  'Document',
  'Oral History',
  'Newspaper/Press',
];

const PAGE_SIZE = 20;
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_RECORDS = 5000;

function normalize(value: string, anyLabel: string): string | undefined {
  if (!value || value === anyLabel) return undefined;
  return value;
}

interface SortableHeaderProps {
  column: ListSubmissionsSort;
  label: string;
  currentSort: ListSubmissionsSort;
  currentDir: 'asc' | 'desc';
  onSort: (column: ListSubmissionsSort) => void;
}

function SortableHeader({
  column,
  label,
  currentSort,
  currentDir,
  onSort,
}: SortableHeaderProps) {
  const active = currentSort === column;
  const arrow = active ? (currentDir === 'asc' ? ' ▲' : ' ▼') : '';
  const ariaSort = active ? (currentDir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th scope="col" aria-sort={ariaSort}>
      <button
        type="button"
        className={`sortable-header${active ? ' sortable-header--active' : ''}`}
        onClick={() => onSort(column)}
        data-testid={`sort-${column}`}
      >
        {label}
        <span aria-hidden="true">{arrow}</span>
      </button>
    </th>
  );
}

export default function SubmissionList({ onSelect, initialFilters }: SubmissionListProps) {
  const [status, setStatus] = useState(initialFilters?.status || 'Any status');
  const [theme, setTheme] = useState(initialFilters?.theme || 'Any theme');
  const [decade, setDecade] = useState(initialFilters?.decade || 'Any decade');
  const [mediaType, setMediaType] = useState(
    initialFilters?.mediaType || 'Any media',
  );
  const [sensitive, setSensitive] = useState(Boolean(initialFilters?.sensitive));
  const [searchInput, setSearchInput] = useState(initialFilters?.search || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchInput);
  const [sort, setSort] = useState<ListSubmissionsSort>(
    initialFilters?.sort || 'createdAt',
  );
  const [dir, setDir] = useState<'asc' | 'desc'>(initialFilters?.dir || 'desc');
  const [page, setPage] = useState(initialFilters?.page || 1);
  const [items, setItems] = useState<ArchiveRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedRowIdx, setFocusedRowIdx] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkFailures, setBulkFailures] = useState<string[]>([]);
  const [bulkDialog, setBulkDialog] = useState<null | 'reject' | 'flag'>(null);
  const [bulkReason, setBulkReason] = useState('');
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  /**
   * F-002: in-place save-filter naming. Replaces the previous
   * `window.prompt()` which (a) cannot be styled, (b) fails silently in
   * some iframes / iPadOS Safari, (c) cannot trap focus for keyboard
   * users. When `savePromptOpen` is true we render an inline input next
   * to the "Save as..." button.
   */
  // F-202: save-prompt state moved into SavedFiltersBar. Kept the
  // surrounding `setSavedFilters` etc. here so the parent owns the
  // canonical filter list.

  const currentUser = useCurrentUser();
  const canExport =
    currentUser?.role === 'lead_moderator' || currentUser?.role === 'admin';

  // Phase 4 responsive: under 600px we render a card layout instead of the
  // table. Both share the same `data-testid="row-..."` so existing tests
  // continue to pass; only one form is in the DOM at a time so checkboxes
  // and labels remain unique.
  const [isNarrow, setIsNarrow] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(max-width: 600px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = () => setIsNarrow(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  const filters = useMemo<ListSubmissionsRequest>(
    () => ({
      status: normalize(status, 'Any status'),
      theme: normalize(theme, 'Any theme'),
      decade: normalize(decade, 'Any decade'),
      mediaType: normalize(mediaType, 'Any media'),
      sensitive: sensitive || undefined,
      search: debouncedSearch || undefined,
      sort,
      dir,
      page,
      pageSize: PAGE_SIZE,
    }),
    [status, theme, decade, mediaType, sensitive, debouncedSearch, sort, dir, page],
  );

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.theme) params.set('theme', filters.theme);
    if (filters.decade) params.set('decade', filters.decade);
    if (filters.mediaType) params.set('mediaType', filters.mediaType);
    if (filters.sensitive) params.set('sensitive', 'true');
    if (filters.search) params.set('search', filters.search);
    if (filters.sort && filters.sort !== 'createdAt') params.set('sort', filters.sort);
    if (filters.dir && filters.dir !== 'desc') params.set('dir', filters.dir);
    const qs = params.toString();
    const target = qs ? `#/submissions?${qs}` : '#/submissions';
    if (window.location.hash !== target && window.location.hash.startsWith('#/submissions')) {
      const url = `${window.location.pathname}${window.location.search}${target}`;
      window.history.replaceState(null, '', url);
    }
  }, [filters]);

  const reload = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listSubmissions(filters)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setLoading(false);
        setSubmissionNavCache(
          res.items.map((r) => r.submissionId),
          JSON.stringify(filters),
          res.items.map((r) => ({ id: r.submissionId, title: r.title || r.submissionId })),
        );
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load submissions');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    listSavedFilters()
      .then((res) => {
        if (!cancelled) setSavedFilters(res.filters);
      })
      .catch(() => {
        // Non-fatal.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Arrow-key navigation across rows. Enter opens the focused row.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (target?.isContentEditable) return;
      if (items.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedRowIdx((idx) => {
          const next = idx === null ? 0 : Math.min(items.length - 1, idx + 1);
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedRowIdx((idx) => {
          const next = idx === null ? 0 : Math.max(0, idx - 1);
          return next;
        });
      } else if (e.key === 'Enter' && focusedRowIdx !== null) {
        const id = items[focusedRowIdx]?.submissionId;
        if (id) {
          e.preventDefault();
          onSelect(id);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [items, focusedRowIdx, onSelect]);

  const clearFilters = () => {
    setStatus('Any status');
    setTheme('Any theme');
    setDecade('Any decade');
    setMediaType('Any media');
    setSensitive(false);
    setSearchInput('');
    setSort('createdAt');
    setDir('desc');
    setPage(1);
  };

  const onSortClick = useCallback(
    (column: ListSubmissionsSort) => {
      if (sort === column) {
        setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSort(column);
        setDir(column === 'createdAt' || column === 'aiConfidence' ? 'desc' : 'asc');
      }
      setPage(1);
    },
    [sort],
  );

  const applyFilter = (f: SavedFilter) => {
    setStatus(f.query.status || 'Any status');
    setTheme(f.query.theme || 'Any theme');
    setDecade(f.query.decade || 'Any decade');
    setMediaType(f.query.mediaType || 'Any media');
    setSensitive(Boolean(f.query.sensitive));
    setSearchInput(f.query.search || '');
    setSort((f.query.sort as ListSubmissionsSort) || 'createdAt');
    setDir(f.query.dir || 'desc');
    setPage(1);
  };

  /**
   * Persist a saved filter using the current filter set. Used by the
   * SavedFiltersBar; we keep the body assembly here so the bar is a
   * pure UI shell.
   */
  const saveCurrentFilters = async (name: string) => {
    try {
      const res = await saveSavedFilter({
        name,
        query: {
          status: filters.status,
          theme: filters.theme,
          decade: filters.decade,
          mediaType: filters.mediaType,
          sensitive: filters.sensitive,
          search: filters.search,
          sort: filters.sort,
          dir: filters.dir,
        },
      });
      setSavedFilters(res.filters);
    } catch (err) {
      setError((err as Error).message || 'Failed to save filter');
    }
  };

  const removeFilter = async (name: string) => {
    try {
      const res = await deleteSavedFilter(name);
      setSavedFilters(res.filters);
    } catch (err) {
      setError((err as Error).message || 'Failed to delete filter');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkApprove = async () => {
    const pendingIds = items
      .filter(
        (i) => selectedIds.has(i.submissionId) && i.moderationStatus === 'pending_review',
      )
      .map((i) => i.submissionId);
    if (pendingIds.length === 0) return;
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: pendingIds.length });
    setBulkFailures([]);
    try {
      let done = 0;
      const failures: string[] = [];
      for (const id of pendingIds) {
        try {
          await moderateSubmission(id, 'approved');
        } catch (err) {
          failures.push(`${id}: ${(err as Error).message || 'failed'}`);
        }
        done += 1;
        setBulkProgress({ done, total: pendingIds.length });
      }
      setBulkFailures(failures);
      if (failures.length === 0) {
        setSelectedIds(new Set());
        toast.success(`Approved ${pendingIds.length} submission${pendingIds.length === 1 ? '' : 's'}.`);
      } else {
        toast.error(`Approved ${pendingIds.length - failures.length} of ${pendingIds.length}; some failed.`);
      }
      setReloadKey((k) => k + 1);
    } finally {
      setBulkBusy(false);
    }
  };

  const openBulkReject = () => {
    setBulkDialog('reject');
    setBulkReason('');
    setBulkFailures([]);
    setBulkProgress(null);
  };

  const openBulkFlag = () => {
    setBulkDialog('flag');
    setBulkReason('');
    setBulkFailures([]);
    setBulkProgress(null);
  };

  const closeBulkDialog = () => {
    if (bulkBusy) return;
    setBulkDialog(null);
  };

  const confirmBulkReject = async () => {
    const rejectableIds = items
      .filter(
        (i) =>
          selectedIds.has(i.submissionId) &&
          (i.moderationStatus === 'pending_review' || i.moderationStatus === 'approved'),
      )
      .map((i) => i.submissionId);
    if (rejectableIds.length === 0) {
      setBulkDialog(null);
      return;
    }
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: rejectableIds.length });
    setBulkFailures([]);
    try {
      let done = 0;
      const failures: string[] = [];
      const note = bulkReason || undefined;
      await Promise.all(
        rejectableIds.map(async (id) => {
          try {
            await moderateSubmission(id, 'rejected', note);
          } catch (err) {
            failures.push(`${id}: ${(err as Error).message || 'failed'}`);
          } finally {
            done += 1;
            setBulkProgress({ done, total: rejectableIds.length });
          }
        }),
      );
      setBulkFailures(failures);
      if (failures.length === 0) {
        setSelectedIds(new Set());
        setBulkDialog(null);
        toast.success(`Rejected ${rejectableIds.length} submission${rejectableIds.length === 1 ? '' : 's'}.`);
      } else {
        toast.error(`Rejected ${rejectableIds.length - failures.length} of ${rejectableIds.length}; some failed.`);
      }
      setReloadKey((k) => k + 1);
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmBulkFlag = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !bulkReason.trim()) {
      setBulkDialog(null);
      return;
    }
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: ids.length });
    setBulkFailures([]);
    try {
      const res = await updateMetadataBulk(ids, { internalNotes: bulkReason });
      const failures = (res.failed || []).map((f) => `${f.submissionId}: ${f.error}`);
      setBulkFailures(failures);
      setBulkProgress({ done: (res.updated || []).length, total: ids.length });
      if (failures.length === 0) {
        setSelectedIds(new Set());
        setBulkDialog(null);
        toast.success(`Flagged ${ids.length} submission${ids.length === 1 ? '' : 's'} with a shared note.`);
      } else {
        toast.error(`Flagged ${(res.updated || []).length} of ${ids.length}; some failed.`);
      }
      setReloadKey((k) => k + 1);
    } catch (err) {
      const msg = (err as Error).message || 'Bulk flag failed';
      setBulkFailures([msg]);
      toast.error(msg);
    } finally {
      setBulkBusy(false);
    }
  };

  /**
   * Page through the API gathering up to EXPORT_MAX_RECORDS rows that match
   * the active filter set, build a CSV, and trigger a browser download.
   * Client-side rather than server: keeps the API surface small and reuses
   * the existing list endpoint that the rest of the workbench already uses.
   */
  const exportCsv = async () => {
    setExportBusy(true);
    setExportError(null);
    try {
      const accumulator: ArchiveRecord[] = [];
      let page = 1;
      let totalSeen = Infinity;
      while (accumulator.length < totalSeen && accumulator.length < EXPORT_MAX_RECORDS) {
        const res = await listSubmissions({
          ...filters,
          page,
          pageSize: EXPORT_PAGE_SIZE,
        });
        if (page === 1) totalSeen = res.total;
        if (res.items.length === 0) break;
        accumulator.push(...res.items);
        page += 1;
        if (accumulator.length >= res.total) break;
      }
      const rows = accumulator.map((r) => {
        const lastAi = r.aiAnalysis && r.aiAnalysis[r.aiAnalysis.length - 1];
        return {
          submissionId: r.submissionId,
          title: r.title || '',
          status: r.moderationStatus,
          createdAt: r.createdAt,
          themes: (r.themes || []).join('; '),
          decades: (r.decades || []).join('; '),
          contributorName: r.contributor?.name || '',
          contributorEmail: r.contributor?.email || '',
          attributionPref: r.contributor?.attributionPref || '',
          pipelineStatus: r.pipeline?.status || '',
          aiConfidence: lastAi ? lastAi.confidence.toFixed(2) : '',
        };
      });
      const csv = buildCsv(
        [
          { key: 'submissionId', label: 'Submission ID' },
          { key: 'title', label: 'Title' },
          { key: 'status', label: 'Status' },
          { key: 'createdAt', label: 'Created At' },
          { key: 'themes', label: 'Themes' },
          { key: 'decades', label: 'Decades' },
          { key: 'contributorName', label: 'Contributor Name' },
          { key: 'contributorEmail', label: 'Contributor Email' },
          { key: 'attributionPref', label: 'Attribution Preference' },
          { key: 'pipelineStatus', label: 'Pipeline Status' },
          { key: 'aiConfidence', label: 'AI Confidence' },
        ],
        rows,
      );
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`pride-submissions-${stamp}.csv`, csv);
    } catch (err) {
      setExportError((err as Error).message || 'Export failed');
    } finally {
      setExportBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasSelection = selectedIds.size > 0;
  const showProgress = bulkBusy && bulkProgress;

  return (
    <div className="submission-list">
      <h1 className="submission-list__heading">Submissions</h1>
      <SavedFiltersBar
        savedFilters={savedFilters}
        onApply={applyFilter}
        onSave={saveCurrentFilters}
        onDelete={removeFilter}
        canExport={canExport}
        onExport={exportCsv}
        exportBusy={exportBusy}
        activeQuery={filters}
      />

      {exportError && (
        <div className="field-error" role="alert" data-testid="export-error">
          {exportError}
        </div>
      )}

      <div className="filter-bar" data-testid="filter-bar">
        <label className="filter-bar__field">
          <span>Status</span>
          <Select options={STATUS_OPTIONS} value={status} onValueChange={(v) => { setStatus(v); setPage(1); }} />
        </label>
        <label className="filter-bar__field">
          <span>Theme</span>
          <Select options={THEME_OPTIONS} value={theme} onValueChange={(v) => { setTheme(v); setPage(1); }} />
        </label>
        <label className="filter-bar__field">
          <span>Decade</span>
          <Select options={DECADE_OPTIONS} value={decade} onValueChange={(v) => { setDecade(v); setPage(1); }} />
        </label>
        <label className="filter-bar__field">
          <span>Media</span>
          <Select options={MEDIA_TYPE_OPTIONS} value={mediaType} onValueChange={(v) => { setMediaType(v); setPage(1); }} />
        </label>
        <Checkbox
          checked={sensitive}
          onCheckedChange={(v) => { setSensitive(v); setPage(1); }}
          label="Sensitive only"
        />
        <label className="filter-bar__search">
          <span className="sr-only">Search</span>
          {/* Mobile keyboard hints: `type=search` shows a magnifying-glass
              key on iOS/Android keyboards; `enterKeyHint=search` swaps the
              "return" key for a "search" button. `autoComplete=off`
              suppresses the moderator's previous query browser history
              popping over the filter bar. */}
          <input
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            spellCheck="false"
            placeholder="Search title, contributor, location, notes..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="search-input"
            aria-label="Search submissions"
          />
        </label>
        <button className="btn btn-secondary" type="button" onClick={clearFilters}>
          Clear filters
        </button>
      </div>

      {hasSelection && (
        // F-106: sticky so the verbs stay reachable after the moderator
        // scrolls down to select items at the bottom of the page.
        <div className="bulk-bar bulk-bar--sticky" data-testid="bulk-bar">
          <span>{selectedIds.size} selected</span>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setSelectedIds(new Set(items.map((i) => i.submissionId)))}
            disabled={bulkBusy}
            data-testid="bulk-select-page"
          >
            Select all {items.length} on page
          </button>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setSelectedIds(new Set())}
            disabled={bulkBusy}
            data-testid="bulk-clear"
          >
            Clear selection
          </button>
          <span className="bulk-bar__divider" aria-hidden="true" />
          <button
            type="button"
            className="btn btn-primary"
            onClick={bulkApprove}
            disabled={bulkBusy}
          >
            {bulkBusy ? 'Approving…' : 'Bulk Approve'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={openBulkReject}
            disabled={bulkBusy}
            data-testid="bulk-reject"
          >
            Bulk Reject
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={openBulkFlag}
            disabled={bulkBusy}
            data-testid="bulk-flag"
          >
            Flag for Review
          </button>
          {showProgress && (
            <span className="bulk-bar__progress" data-testid="bulk-progress">
              {bulkProgress!.done} of {bulkProgress!.total} done
            </span>
          )}
        </div>
      )}

      {bulkFailures.length > 0 && (
        <div className="bulk-bar__failures" role="alert" data-testid="bulk-failures">
          <strong>Some operations failed:</strong>
          <ul>
            {bulkFailures.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        // F-008: render skeleton rows (or cards on mobile) instead of a
        // bare "Loading submissions…" string. aria-busy on the host so
        // SR users hear that we're working.
        isNarrow ? (
          <ul
            className="data-cards"
            aria-busy="true"
            data-testid="list-loading"
            aria-label="Loading submissions"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="data-card" aria-hidden="true">
                <Skeleton variant="block" />
              </li>
            ))}
          </ul>
        ) : (
          <table
            className="data-table"
            aria-busy="true"
            data-testid="list-loading"
          >
            <thead>
              <tr>
                <th aria-label="Loading" colSpan={9} />
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cells={9} />
              ))}
            </tbody>
          </table>
        )
      ) : error ? (
        <ErrorState
          message="We could not load the submissions list."
          detail={error}
          onRetry={reload}
          testId="list-error"
        />
      ) : items.length === 0 ? (
        // F-108: empty state with a clear filters action so a user
        // landing on a stale URL has an immediate next step.
        <EmptyState
          testId="list-empty"
          title="No submissions match the current filters."
          description="Try clearing filters or adjusting your search."
          action={
            <button
              type="button"
              className="btn btn-secondary"
              onClick={clearFilters}
              data-testid="list-empty-clear-filters"
            >
              Clear filters
            </button>
          }
        />
      ) : isNarrow ? (
        <ul className="data-cards" data-testid="submission-cards" aria-label="Submissions">
          {items.map((item, idx) => {
            const lastConfidence = item.aiAnalysis?.length
              ? item.aiAnalysis[item.aiAnalysis.length - 1].confidence
              : null;
            const focused = focusedRowIdx === idx;
            return (
              <li
                key={item.submissionId}
                className={`data-card${focused ? ' data-card--focused' : ''}`}
                data-testid={`row-${item.submissionId}`}
              >
                <div className="data-card__select">
                  <input
                    type="checkbox"
                    aria-label={`Select ${item.title}`}
                    checked={selectedIds.has(item.submissionId)}
                    onChange={() => toggleSelect(item.submissionId)}
                  />
                </div>
                <button
                  type="button"
                  className="data-card__body"
                  onClick={() => onSelect(item.submissionId)}
                  aria-label={`Open ${item.title}`}
                >
                  <div className="data-card__title">{item.title}</div>
                  <div className="data-card__meta">
                    <StatusBadge status={item.moderationStatus} />
                    {item.mediaType && <span className="chip">{item.mediaType}</span>}
                    {item.sensitive && (
                      <span className="sensitivity-icon" aria-label="sensitive" title="Sensitive">!</span>
                    )}
                  </div>
                  <div className="data-card__sub">
                    <span>{formatDate(item.createdAt)}</span>
                    {lastConfidence !== null && (
                      <span>AI {Math.round(lastConfidence * 100)}%</span>
                    )}
                    <span className={`pipeline-tag pipeline-tag--${item.pipeline?.status || 'unknown'}`}>
                      {item.pipeline?.status || 'unknown'}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <>
          <table className="data-table" data-testid="submission-table">
            <thead>
              <tr>
                <th scope="col" aria-label="Select" />
                <SortableHeader column="title" label="Title" currentSort={sort} currentDir={dir} onSort={onSortClick} />
                <SortableHeader column="moderationStatus" label="Status" currentSort={sort} currentDir={dir} onSort={onSortClick} />
                <th scope="col">Media Type</th>
                <th scope="col">Decade</th>
                <SortableHeader column="aiConfidence" label="AI" currentSort={sort} currentDir={dir} onSort={onSortClick} />
                <th scope="col" aria-label="Sensitivity" />
                <SortableHeader column="createdAt" label="Submitted" currentSort={sort} currentDir={dir} onSort={onSortClick} />
                <SortableHeader column="pipelineStatus" label="Pipeline" currentSort={sort} currentDir={dir} onSort={onSortClick} />
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const lastConfidence = item.aiAnalysis?.length
                  ? item.aiAnalysis[item.aiAnalysis.length - 1].confidence
                  : null;
                const focused = focusedRowIdx === idx;
                return (
                  <tr
                    key={item.submissionId}
                    className={`data-table__row${focused ? ' data-table__row--focused' : ''}`}
                    onClick={() => onSelect(item.submissionId)}
                    aria-selected={focused}
                    data-testid={`row-${item.submissionId}`}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${item.title}`}
                        checked={selectedIds.has(item.submissionId)}
                        onChange={() => toggleSelect(item.submissionId)}
                      />
                    </td>
                    <td>
                      {item.title}
                      {item.claim && (
                        <Tooltip content={`Claimed by ${item.claim.userEmail}`}>
                          <span
                            className="claim-chip"
                            data-testid={`claim-chip-${item.submissionId}`}
                            tabIndex={0}
                          >
                            claimed
                          </span>
                        </Tooltip>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={item.moderationStatus} />
                    </td>
                    <td>{item.mediaType || '—'}</td>
                    <td>{item.decades?.join(', ') || '—'}</td>
                    <td>
                      {lastConfidence !== null ? (
                        <span data-testid={`ai-conf-${item.submissionId}`}>
                          {Math.round(lastConfidence * 100)}%
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {item.sensitive ? (
                        <span aria-label="sensitive" title="Sensitive" className="sensitivity-icon">!</span>
                      ) : (
                        ''
                      )}
                    </td>
                    <td>
                      {/* F-305: surface relative time with absolute on hover. */}
                      <Tooltip content={formatDateTime(item.createdAt)}>
                        <span tabIndex={0} data-testid={`created-${item.submissionId}`}>
                          {relativeTime(item.createdAt)}
                        </span>
                      </Tooltip>
                    </td>
                    <td>
                      <span className={`pipeline-tag pipeline-tag--${item.pipeline?.status || 'unknown'}`}>
                        {item.pipeline?.status || 'unknown'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      <div className="pagination" data-testid="pagination">
        <button
          type="button"
          className="btn btn-secondary"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </button>
        <span className="pagination__status">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </button>
      </div>

      <Dialog open={bulkDialog !== null} onOpenChange={(o) => !o && closeBulkDialog()}>
        {bulkDialog && (
          <DialogContent>
            <DialogTitle>
              {bulkDialog === 'reject' ? 'Bulk Reject' : 'Flag for Review'}
            </DialogTitle>
            <DialogDescription>
              {bulkDialog === 'reject'
                ? `Reject ${selectedIds.size} submission${selectedIds.size === 1 ? '' : 's'}. The same note will be sent with every rejection.`
                : `Add a shared internal note to ${selectedIds.size} submission${selectedIds.size === 1 ? '' : 's'}.`}
            </DialogDescription>
            {bulkDialog === 'reject' && (
              <label className="form-group">
                <span>Template (optional)</span>
                <Select
                  options={['Custom...', ...REJECT_REASON_TEMPLATES.map((t) => t.label)]}
                  value="Custom..."
                  onValueChange={(v) => {
                    const tpl = REJECT_REASON_TEMPLATES.find((t) => t.label === v);
                    if (tpl) setBulkReason(tpl.body);
                  }}
                />
              </label>
            )}
            <label className="form-group">
              <span>
                {bulkDialog === 'reject' ? 'Reason (sent to contributor)' : 'Internal note'}
              </span>
              <textarea
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                data-testid="bulk-reason"
                rows={4}
                placeholder={bulkDialog === 'reject' ? 'Why is this being rejected?' : 'Note for the team'}
              />
            </label>
            {showProgress && (
              <div className="bulk-bar__progress" data-testid="bulk-dialog-progress">
                {bulkProgress!.done} of {bulkProgress!.total} done
              </div>
            )}
            {bulkFailures.length > 0 && (
              <div className="field-error" role="alert">
                <ul>
                  {bulkFailures.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="dlg-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeBulkDialog}
                disabled={bulkBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={bulkDialog === 'reject' ? confirmBulkReject : confirmBulkFlag}
                disabled={bulkBusy || (bulkDialog === 'flag' && !bulkReason.trim())}
                data-testid="bulk-confirm"
              >
                {bulkBusy ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
